
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import type { Socket } from 'socket.io-client';
import {
  useTicketsStore,
  TicketChat,
  useTicketMailBridge,
  useTicketMedia,
  MediaTab,
  LinksTab,
  FilesTab,
  LegacyFilesTab,
} from '@/features/tickets';
import { useAuthStore } from '@/features/auth';
import { useMassIncidentUi } from '@/features/mass-incidents';
import { RichText } from '@/features/wiki';
import {
  useNotificationsStore,
  useHasUnreadForTicket,
  usePrefetchTicketMute,
} from '@/features/notifications';
import { ticketsApi } from '@/shared/api/tickets';
import { apiErrorText, ApiRequestError, kpiApi, acquireSocket, releaseSocket, joinTicketRoom, type KpiComplexityLevel } from '@/shared/api';
import type { TemplateContext } from '@/features/tickets/ui/TemplatePanel';
import { countUnreadNotes } from '@/features/tickets/ui/chat/chat-sync';
import {
  ticketStatusLabel,
  TICKET_PRIORITY_LABELS,
  isTicketFinal,
  WITHDRAWABLE_STATUSES,
  type TicketPriority,
  MassIncidentIcon,
  EmailIcon,
} from '@/shared/config';
import type {
  TicketMessage,
  TicketDetailData,
  TicketApproval,
  TicketAssignee,
  AllowedTransition,
  ApprovalRoundProgress,
} from '@/entities/ticket';
import { formatWorkMinutes, slaChipTitle, slaProgress, slaShortLabel, slaTone } from '@/entities/ticket';
import { AlertCircle, ArrowLeft, CheckCircle2, MessageSquare } from 'lucide-react';
import {
  Avatar,
  Button,
  ErrorState,
  Scroller,
  StatusBadge,
  Tabs,
  TabsList,
  TabsPanel,
  TabsTab,
  TabsCount,
  confirmDialog,
  toast,
} from '@/shared/ui';
import { TicketHistory } from '@/widgets/TicketHistory';
import { DetailBar } from '@/widgets/DetailBar';
import { cn } from '@/shared/lib/cn';
import { useSkeletonGate } from '@/shared/lib/useSkeletonGate';
import { useMediaQuery } from '@/shared/lib/useMediaQuery';
import { RequesterContacts } from './RequesterContacts';
import {
  descriptionWithoutForm,
  getUserDisplayName,
  hasFormAnswers,
  slaReactionText,
  slaStateCaption,
} from './lib';
import { FormAnswers } from './FormAnswers';
import { TicketDetailSkeleton } from './TicketDetailSkeleton';
import {
  RequesterDetails,
  ServiceLine,
  SlaHourglass,
  VipAvatarFrame,
  VipPill,
  LegacyCard,
  PeopleCard,
  PriorityMark,
  SlaCard,
} from './SidebarCards';
import { FolderGrantsCard } from './FolderGrantsCard';
import { ApprovalsSection } from './ApprovalsSection';
import { TasksSection } from './TasksSection';
import { ResolutionCallout } from './ResolutionCallout';
import { ReassignAssigneeDialog } from './ReassignAssigneeDialog';
import { ComplexityRequestCallout } from './ComplexityRequestCallout';
import { TicketActionsBar, TicketActionsProvider } from './TicketActions';
import { FlowStrip } from './FlowStrip';
import { MailThreadCard } from './MailThreadCard';
import styles from './TicketDetail.module.css';

export const TicketDetailPage: React.FC<{
  ticketId?: string;
  onTicketGone?: () => void;
}> = ({ ticketId: ticketIdProp, onTicketGone }) => {
  const params = useParams<{ id: string }>();
  const id = ticketIdProp ?? params.id;
  return <TicketDetailContent key={id ?? ''} id={id} onTicketGone={onTicketGone} />;
};

const SHOW_FLOW_STRIP = false;

function backSection(backTo: string): string {
  if (backTo.startsWith('/approvals')) return 'Согласования';
  if (backTo.startsWith('/sprints')) return 'Спринты';
  if (backTo.startsWith('/tickets/workspace')) return 'Рабочий стол';
  if (backTo.startsWith('/tickets')) return 'Обращения';
  return 'Назад';
}

const TicketDetailContent: React.FC<{
  id: string | undefined;
  onTicketGone?: () => void;
}> = ({ id, onTicketGone }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = (location.state as { from?: string } | null)?.from ?? '/tickets';
  const canGoBack = location.key !== 'default';
  const leaveTicket = () => (onTicketGone ? onTicketGone() : navigate(backTo));
  const leaveTicketRef = useRef(leaveTicket);
  leaveTicketRef.current = leaveTicket;
  const currentTicket = useTicketsStore((s) => s.currentTicket);
  const isLoading = useTicketsStore((s) => s.currentLoading);
  const { phase: skeletonPhase, reveal } = useSkeletonGate(Boolean(currentTicket));
  const ticketError = useTicketsStore((s) => s.currentError);
  const fetchTicket = useTicketsStore((s) => s.fetchTicket);
  const clearCurrent = useTicketsStore((s) => s.clearCurrent);
  const user = useAuthStore((s) => s.user);
  const hasRole = useAuthStore((s) => s.hasRole);
  const isOwnTicket = !!user?.id && currentTicket?.creatorId === user.id;
  const isAdminRole = hasRole('admin');
  const isStaffRole = hasRole('agent') || hasRole('senior_agent') || isAdminRole;
  const isAdmin = isAdminRole;
  const isAgent = isStaffRole;
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canManageIncident = isStaffRole && hasPermission('change_status');
  const openIncidentPanel = useMassIncidentUi((s) => s.openPanel);
  const isFrozenTicket = isTicketFinal(currentTicket);
  const canReassignGroup = hasPermission('reassign_group') && !isFrozenTicket;
  const canAssign = hasPermission('assign_ticket') && !isFrozenTicket;
  const canReassignAssignee =
    hasPermission('reassign_ticket') && !isFrozenTicket;
  const canRemoveAssignee = canAssign || canReassignAssignee;
  const canAddParticipant =
    hasPermission('can_add_chat_participants') && !isFrozenTicket;
  const canRemoveApproval = hasPermission('change_status') && !isOwnTicket && !isFrozenTicket;
  const canRequestApproval = canRemoveApproval;
  const canRateComplexity =
    hasPermission('rate_ticket_complexity') && !isFrozenTicket;
  const canViewProfiles = hasPermission('view_user_profiles');
  const canViewNotes = hasPermission('view_ticket_chat');
  const mailEnabled = isStaffRole && hasPermission('view_tickets');
  const ticketMail = useTicketMailBridge({
    ticketId: currentTicket?.id,
    enabled: mailEnabled,
    canMove: mailEnabled && !isFrozenTicket,
  });

  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [liveMessages, setLiveMessages] = useState<TicketMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [chatForbidden, setChatForbidden] = useState(false);
  const [chatError, setChatError] = useState(false);
  const [chatReloadKey, setChatReloadKey] = useState(0);
  const [chatTab, setChatTab] = useState<'chat' | 'notes' | 'history'>('chat');
  const [notes, setNotes] = useState<TicketMessage[]>([]);
  const [liveNotes, setLiveNotes] = useState<TicketMessage[] | null>(null);
  const [notesReadAt, setNotesReadAt] = useState<string | null>(null);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState(false);
  const [notesReloadKey, setNotesReloadKey] = useState(0);
  const [notesSupported, setNotesSupported] = useState<boolean | null>(null);
  const showNotes = canViewNotes && notesSupported !== false;
  const [historyFilterSlot, setHistoryFilterSlot] = useState<HTMLDivElement | null>(null);
  const [approvals, setApprovals] = useState<TicketApproval[]>([]);
  const [approvalRounds, setApprovalRounds] = useState<ApprovalRoundProgress[]>([]);
  const [removingRound, setRemovingRound] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState<string | null>(null);
  const [removingApprovalId, setRemovingApprovalId] = useState<string | null>(null);
  const [removingAssigneeId, setRemovingAssigneeId] = useState<string | null>(null);
  const [reassigningAssignee, setReassigningAssignee] = useState<TicketAssignee | null>(null);
  const [transitions, setTransitions] = useState<AllowedTransition[]>([]);
  const [complexityLevels, setComplexityLevels] = useState<KpiComplexityLevel[]>([]);

  const markTicketNotificationsRead = useNotificationsStore((s) => s.markTicketAsRead);
  const hasUnreadTicketNotifications = useHasUnreadForTicket(id);
  const setActiveTicketId = useNotificationsStore((s) => s.setActiveTicketId);

  useEffect(() => {
    setActiveTicketId(id ?? null);
    return () => setActiveTicketId(null);
  }, [id, setActiveTicketId]);

  useEffect(() => {
    if (id) void markTicketNotificationsRead(id);
  }, [id, markTicketNotificationsRead]);

  useEffect(() => {
    if (id && hasUnreadTicketNotifications) void markTicketNotificationsRead(id);
  }, [id, hasUnreadTicketNotifications, markTicketNotificationsRead]);

  useEffect(() => {
    if (id) fetchTicket(id);
    return () => clearCurrent();
  }, [id, fetchTicket, clearCurrent]);

  usePrefetchTicketMute(id);

  const narrow = useMediaQuery('(max-width: 960px)');

  const wsRef = useRef<Socket | null>(null);
  useEffect(() => {
    if (!id || !user?.id) return;
    let cancelled = false;
    let refetchTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleRefetch = () => {
      if (refetchTimer) clearTimeout(refetchTimer);
      refetchTimer = setTimeout(() => {
        refetchTimer = null;
        if (!cancelled) fetchTicket(id);
      }, 300);
    };
    const onTicketUpdated = (payload: { ticket?: { id?: string } }) => {
      if (!payload?.ticket || payload.ticket.id !== id) return;
      scheduleRefetch();
    };
    const onTicketDeleted = (payload: { ticketId?: string }) => {
      if (payload?.ticketId === id) leaveTicketRef.current();
    };
    let wasConnected = false;
    const onConnect = () => {
      if (wasConnected) scheduleRefetch();
      wasConnected = true;
    };
    const leaveRoom = joinTicketRoom(id);
    void acquireSocket().then((socket) => {
      if (cancelled || !socket) return;
      wsRef.current = socket;
      wasConnected = socket.connected;
      socket.on('ticket_updated', onTicketUpdated);
      socket.on('ticket_deleted', onTicketDeleted);
      socket.on('connect', onConnect);
    });
    return () => {
      cancelled = true;
      if (refetchTimer) clearTimeout(refetchTimer);
      const s = wsRef.current;
      if (s) {
        s.off('ticket_updated', onTicketUpdated);
        s.off('ticket_deleted', onTicketDeleted);
        s.off('connect', onConnect);
        wsRef.current = null;
      }
      leaveRoom();
      releaseSocket();
    };
  }, [id, user?.id, fetchTicket]);

  useEffect(() => {
    if (!id || !user?.id) return;
    let cancelled = false;
    setMessagesLoading(true);
    setChatForbidden(false);
    setChatError(false);
    setLiveMessages([]);
    ticketsApi
      .getMessages(id, { kind: 'chat' })
      .then((res) => {
        if (cancelled) return;
        setMessages(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setMessages([]);
        if (err instanceof ApiRequestError && err.status === 403) setChatForbidden(true);
        else setChatError(true);
      })
      .finally(() => {
        if (!cancelled) setMessagesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, user?.id, chatReloadKey]);

  useEffect(() => {
    if (!id || !user?.id || !canViewNotes) return;
    let cancelled = false;
    setNotesLoading(true);
    setNotesError(false);
    setLiveNotes(null);
    ticketsApi
      .getMessages(id, { kind: 'notes' })
      .then((res) => {
        if (cancelled) return;
        const supported = typeof res.meta?.total === 'number';
        setNotesSupported(supported);
        setNotes(supported && Array.isArray(res.data) ? res.data : []);
        setNotesReadAt(res.meta?.lastReadAt ?? null);
      })
      .catch(() => {
        if (cancelled) return;
        setNotes([]);
        setNotesError(true);
      })
      .finally(() => {
        if (!cancelled) setNotesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, user?.id, canViewNotes, notesReloadKey]);

  const notesList = liveNotes ?? notes;
  const notesUnread = useMemo(
    () => countUnreadNotes(notesList, notesReadAt, user?.id),
    [notesList, notesReadAt, user?.id],
  );
  const advanceNotesRead = useCallback((at: string) => {
    setNotesReadAt((prev) => (prev && prev >= at ? prev : at));
  }, []);
  const addNoteFromTemplate = useCallback(
    (note: TicketMessage) => {
      setLiveNotes((prev) => {
        const base = prev ?? notes;
        return base.some((n) => n.id === note.id) ? base : [...base, note];
      });
    },
    [notes],
  );

  const ticket = currentTicket as TicketDetailData | null;
  const ticketStatus = ticket?.status;

  useEffect(() => {
    if (!id || !user?.id || !ticketStatus) return;
    ticketsApi
      .getAllowedTransitions(id)
      .then((res) => setTransitions(Array.isArray(res.data) ? res.data : []))
      .catch(() => setTransitions([]));
  }, [id, user?.id, ticketStatus]);

  useEffect(() => {
    if (!isStaffRole) return;
    kpiApi
      .complexityLevels()
      .then((res) => setComplexityLevels(res.data ?? []))
      .catch(() => setComplexityLevels([]));
  }, [isStaffRole]);

  const chatList = liveMessages.length ? liveMessages : messages;
  const sidebarMessages = useMemo(
    () =>
      notesList.length
        ? [...chatList, ...notesList].sort((a, b) => a.createdAt.localeCompare(b.createdAt))
        : chatList,
    [chatList, notesList],
  );
  const sidebarMedia = useTicketMedia(sidebarMessages);
  const legacyFiles = useMemo(() => ticket?.legacy?.files ?? [], [ticket?.legacy?.files]);
  const mediaTabs = useMemo(
    () =>
      [
        {
          value: 'media',
          label: 'Медиа',
          count: sidebarMedia.media.length,
        },
        {
          value: 'links',
          label: 'Ссылки',
          count: sidebarMedia.links.length,
        },
        {
          value: 'files',
          label: 'Файлы',
          count: sidebarMedia.rows.length,
        },
        legacyFiles.length > 0 && { value: 'legacy', label: 'Итилиум', count: legacyFiles.length },
      ].filter(Boolean) as Array<{ value: string; label: string; count: number }>,
    [sidebarMedia.media.length, sidebarMedia.links.length, sidebarMedia.rows.length, legacyFiles.length],
  );
  const assignees = (ticket?.assignees ?? []) as TicketAssignee[];
  const participants = ticket?.participants ?? [];
  const ticketApprovals = ticket?.approvals ?? approvals;
  const approvalsPending =
    approvalRounds[0]?.status === 'pending' || ticketApprovals.some((a) => a.status === 'pending');
  const myPendingApprovals = ticketApprovals.filter(
    (a) => a.status === 'pending' && a.approverId === user?.id,
  );
  const myApproval = myPendingApprovals.length === 1 ? myPendingApprovals[0] : null;

  useEffect(() => {
    if (!id || !ticket?.approvals?.length) return;
    setApprovals(ticket.approvals);
  }, [id, ticket?.approvals]);

  const loadRounds = useCallback(async () => {
    if (!id) return;
    try {
      const res = await ticketsApi.getApprovalRounds(id);
      setApprovalRounds(res.data);
    } catch {
    }
  }, [id]);

  const approvalsSignature = ticketApprovals.map((a) => `${a.id}:${a.status}`).join('|');
  useEffect(() => {
    loadRounds();
  }, [loadRounds, approvalsSignature, ticket?.status]);

  const removeRound = useCallback(
    async (roundId: string) => {
      if (!id) return;
      const ok = await confirmDialog({
        title: 'Удалить согласование комитета?',
        description: 'Все запросы этого круга пропадут вместе с доступом согласующих к заявке.',
        confirmLabel: 'Удалить',
        danger: true,
      });
      if (!ok) return;
      setRemovingRound(true);
      try {
        await ticketsApi.removeApprovalRound(id, roundId);
        setApprovalRounds((prev) => prev.filter((r) => r.roundId !== roundId));
        setApprovals((prev) => prev.filter((a) => a.roundId !== roundId));
        toast.success('Согласование комитета удалено');
        await Promise.all([loadRounds(), fetchTicket(id)]);
      } catch (err) {
        toast.error(apiErrorText(err, 'Не удалось удалить согласование'));
      } finally {
        setRemovingRound(false);
      }
    },
    [id, loadRounds, fetchTicket],
  );

  const decideApproval = useCallback(
    async (approvalId: string, approve: boolean, comment: string) => {
      if (!id) return;
      if (!approve && !comment) {
        toast.error('Укажите причину отклонения');
        return;
      }
      setApprovalLoading(approvalId);
      try {
        const res = approve
          ? await ticketsApi.approveApproval(id, approvalId, comment || undefined)
          : await ticketsApi.rejectApproval(id, approvalId, comment);
        const updated = res.data as TicketApproval;
        setApprovals((prev) => prev.map((a) => (a.id === approvalId ? updated : a)));
        fetchTicket(id);
      } catch (err) {
        toast.error(apiErrorText(err, 'Ошибка решения по согласованию'));
      } finally {
        setApprovalLoading(null);
      }
    },
    [id, fetchTicket],
  );
  const handleApprove = useCallback(
    (aid: string, comment: string) => decideApproval(aid, true, comment),
    [decideApproval],
  );
  const handleReject = useCallback(
    (aid: string, comment: string) => decideApproval(aid, false, comment),
    [decideApproval],
  );

  const removeApproval = useCallback(
    async (approvalId: string) => {
      if (!id) return;
      const target = (ticket?.approvals ?? approvals).find((a) => a.id === approvalId);
      if (target?.roundId) {
        await removeRound(target.roundId);
        return;
      }
      setRemovingApprovalId(approvalId);
      try {
        await ticketsApi.removeApproval(id, approvalId);
        setApprovals((prev) => prev.filter((a) => a.id !== approvalId));
        toast.success('Согласование удалено');
        fetchTicket(id);
      } catch (err) {
        toast.error(apiErrorText(err, 'Ошибка удаления согласования'));
      } finally {
        setRemovingApprovalId(null);
      }
    },
    [id, fetchTicket, ticket?.approvals, approvals, removeRound],
  );

  const handleRemoveAssignee = useCallback(
    async (userId: string) => {
      if (!id) return;
      const ok = await confirmDialog({
        title: 'Снять ответственного?',
        description: 'Пользователь перестанет быть исполнителем заявки.',
        confirmLabel: 'Снять',
        danger: true,
      });
      if (!ok) return;
      setRemovingAssigneeId(userId);
      try {
        await ticketsApi.removeAssignee(id, userId);
        fetchTicket(id);
      } catch {
      } finally {
        setRemovingAssigneeId(null);
      }
    },
    [id, fetchTicket],
  );

  if (!isLoading && !currentTicket && ticketError) {
    return (
      <div className={styles.page}>
        <div className={styles.loadError}>
          <ErrorState message={ticketError} onRetry={() => id && fetchTicket(id)} />
          <div className={styles.loadErrorActions}>
            <Button variant="ghost" type="button" onClick={leaveTicket}>
              <ArrowLeft size={16} /> Назад
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentTicket || skeletonPhase !== 'content') {
    return (
      <TicketDetailSkeleton
        backTo={backTo}
        backLabel={backSection(backTo)}
        leaving={skeletonPhase === 'leaving'}
      />
    );
  }

  const ticketTitle =
    (ticket as { subject?: string; title?: string }).subject ?? (ticket as { title?: string }).title;
  const status = ticket!.status;
  const priority = ticket!.priority as TicketPriority;
  const isActiveTicket = !isTicketFinal(ticket);
  const isAssigneeOfTicket = assignees.some((a) => a.userId === user?.id);
  const isOutcomeStatus = ['resolved', 'not_confirmed', 'rejected'].includes(status);
  const canTake =
    isAgent && !isAssigneeOfTicket && isActiveTicket && !isOutcomeStatus;
  const canWithdraw = user?.id === ticket!.creatorId && WITHDRAWABLE_STATUSES.includes(status);
  const templateContext: TemplateContext | null = isAgent
    ? {
        ticketId: ticket!.id,
        ticketSubject: String(ticketTitle ?? ''),
        ticketStatus: ticketStatusLabel(status, ticket!.statusLabel),
        ticketPriority: TICKET_PRIORITY_LABELS[priority] ?? priority,
        creatorName: getUserDisplayName(ticket!.creator, ticket!.creatorId),
        actorName: user?.name || user?.email || 'Сотрудник поддержки',
        groupId: ticket!.assignedGroupId ?? null,
      }
    : null;

  const needsRequesterReply = status === 'waiting_for_requester' && user?.id === ticket!.creatorId;

  const createdLabel = new Date(ticket!.createdAt).toLocaleString('ru', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const descriptionText = descriptionWithoutForm(ticket!);

  const autoSubject =
    isStaffRole && !!ticketTitle && ticketTitle === ticket!.snapshot?.subcategory?.name;

  const headNode = (
    <header className={styles.head}>
      {isStaffRole && <ServiceLine ticket={ticket!} serviceInTitle={autoSubject} />}
      <h1 className={styles.title}>{ticketTitle}</h1>
      { }
      <div className={styles.headMeta}>
        {ticket!.number != null && <span className={styles.headMetaText}>№{ticket!.number}</span>}
        <time className={styles.headMetaText} dateTime={ticket!.createdAt} title="Создана">
          {createdLabel}
        </time>
        {ticket!.assignedGroup && (
          <span className={styles.groupChip} title={ticket!.assignedGroup.name}>
            {ticket!.assignedGroup.name}
          </span>
        )}
        <StatusBadge status={status} label={ticket!.statusLabel} dot />
        {isStaffRole && (priority === 'high' || priority === 'critical') && (
          <span className={styles.priorityHigh}>{TICKET_PRIORITY_LABELS[priority]}</span>
        )}
        {ticket!.massIncident &&
          (canManageIncident ? (
            <button
              type="button"
              className={cn(
                styles.incidentChip,
                ticket!.massIncident.status === 'resolved' && styles.incidentChipResolved,
              )}
              title="Открыть сбой: состав, ответ всем, «устранён»"
              onClick={() => openIncidentPanel(ticket!.massIncident!.id)}
            >
              <MassIncidentIcon size={12} aria-hidden />
              <span className={styles.incidentChipTitle}>{ticket!.massIncident.title}</span>
            </button>
          ) : (
            <span className={styles.priorityHigh} title={ticket!.massIncident.title}>
              <MassIncidentIcon size={12} aria-hidden /> Массовый сбой
            </span>
          ))}
        {ticket!.channel === 'email' && (
          <span className={styles.groupChip} title="Заявка пришла письмом">
            <EmailIcon size={12} aria-hidden /> Из почты
          </span>
        )}
      </div>
    </header>
  );

  const sideCards = (
    <>
    { }
    {!narrow && (
      <SlaCard ticket={ticket!} isStaffViewer={isStaffRole} complexityLevels={complexityLevels} />
    )}


    {!narrow && (
      <PeopleCard
        assignees={assignees}
        participants={participants}
        creator={ticket!.creator}
        creatorId={ticket!.creatorId}
        canReassignAssignee={canReassignAssignee}
        canRemoveAssignee={canRemoveAssignee}
        canViewProfiles={canViewProfiles}
        removingUserId={removingAssigneeId}
        onRemoveAssignee={handleRemoveAssignee}
        onReassignAssignee={setReassigningAssignee}
        requesterVip={!!ticket!.requesterVip}
        requesterDetails={
          <RequesterDetails ticket={ticket!} isStaffViewer={isStaffRole} isAgent={isStaffRole} />
        }
      />
    )}

    { }

    { }
    {id && (
      <TasksSection
        ticketId={id}
        ticketStatus={ticket!.status}
        ticketFinal={isTicketFinal(ticket)}
        plan={ticket!.plan}
        development={ticket!.development}
        onPlanChanged={() => fetchTicket(id)}
      />
    )}

    { }
    {!approvalsPending && (approvalRounds[0]?.levels.length || ticketApprovals.length > 0) ? (
      <ApprovalsSection
        round={approvalRounds[0]?.levels.length ? approvalRounds[0] : null}
        approvals={ticketApprovals}
        currentUserId={user?.id}
        loadingId={approvalLoading}
        canRemove={canRemoveApproval}
        canViewProfiles={canViewProfiles}
        removingId={removingApprovalId}
        removingRound={removingRound}
        onApprove={handleApprove}
        onReject={handleReject}
        onRemove={removeApproval}
        onRemoveRound={removeRound}
        liftedApprovalId={myApproval?.id ?? null}
      />
    ) : null}


    {isStaffRole && ticket!.legacy && <LegacyCard legacy={ticket!.legacy} />}
    </>
  );

  const requesterName = getUserDisplayName(ticket!.creator, ticket!.creatorId);
  const stripSla = ticket!.sla;
  const stripTone = stripSla ? slaTone(stripSla) : null;
  const stripReaction = ticket!.slaReaction ?? null;
  const reactionText = slaReactionText(stripReaction);
  const stripLevel = complexityLevels.length
    ? complexityLevels.find((l) => l.class === (ticket!.complexityClass ?? 1))
    : undefined;

  return (
    <div className={cn(styles.page, reveal && styles.reveal)}>
      <TicketActionsProvider
        ticket={ticket!}
        canTake={canTake}
        canWithdraw={canWithdraw}
        transitions={transitions}
        templateContext={templateContext}
        canAssign={canAssign}
        canAddParticipant={canAddParticipant}
        canReassignGroup={canReassignGroup}
        canRequestApproval={canRequestApproval}
        frozen={isFrozenTicket}
        isAgent={isAgent}
        isAdmin={isAdmin}
        canRateComplexity={canRateComplexity}
        isAssigneeOfTicket={isAssigneeOfTicket}
        complexityLevels={complexityLevels}
        myApproval={myApproval}
        onApprove={handleApprove}
        onReject={handleReject}
        onApprovalCreated={(a) => setApprovals((prev) => [a, ...prev])}
        onRoundStarted={loadRounds}
        onDeleted={leaveTicket}
      >
      { }
      <DetailBar
        className={styles.detailBar}
        backTo={backTo}
        onBack={(e) => {
          if (canGoBack && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
            e.preventDefault();
            navigate(-1);
          }
        }}
        backLabel={backSection(backTo)}
        extra={<TicketActionsBar />}
        noChatButton
      />

      { }
      {SHOW_FLOW_STRIP && (
        <FlowStrip
          ticketId={ticket!.id}
          status={status}
          statusLabel={ticket!.statusLabel}
          workflowVersionId={(ticket as { workflowVersionId?: string | null }).workflowVersionId ?? null}
          pendingApproval={ticketApprovals.some((a) => a.status === 'pending')}
        />
      )}

      { }
      {narrow && headNode}

      { }
      {narrow && (
        <div className={cn(styles.card, styles.factsCard)}>
          {ticket!.creatorId && (
            <div className={styles.factsRequester}>
              <VipAvatarFrame vip={!!ticket!.requesterVip}>
                <Avatar
                  className={styles.factsAvatar}
                  name={requesterName}
                  seed={ticket!.creatorId}
                  src={ticket!.creator?.avatarUrl}
                />
              </VipAvatarFrame>
              <div className={styles.factsRequesterText}>
                <span className={styles.factsNameRow}>
                  {canViewProfiles ? (
                    <Link to={`/users/${ticket!.creatorId}`} className={styles.factsRequesterName}>
                      {requesterName}
                    </Link>
                  ) : (
                    <span className={styles.factsRequesterName}>{requesterName}</span>
                  )}
                  {ticket!.requesterVip && <VipPill />}
                </span>
                <span className={styles.factsRequesterRole}>
                  {['заявитель', ticket!.creator?.department].filter(Boolean).join(' · ')}
                </span>
              </div>
            </div>
          )}
          {isStaffRole && (
            <RequesterContacts
              phone={ticket!.creatorPhone}
              email={ticket!.creator?.email}
              className={styles.factsContacts}
            />
          )}
          { }
          {[
            ...assignees.map((a) => ({ key: a.id, userId: a.userId, user: a.user, role: 'ответственный' })),
            ...participants.map((pt) => ({
              key: pt.id,
              userId: pt.userId,
              user: pt.user,
              role: pt.kind === 'approver' ? 'согласующий' : 'участник чата',
            })),
          ].map((m) => (
            <div key={m.key} className={styles.factsRequester}>
              <Avatar
                className={styles.factsAvatar}
                name={getUserDisplayName(m.user, m.userId)}
                seed={m.userId}
                src={m.user?.avatarUrl}
              />
              <div className={styles.factsRequesterText}>
                {canViewProfiles ? (
                  <Link to={`/users/${m.userId}`} className={styles.factsRequesterName}>
                    {getUserDisplayName(m.user, m.userId)}
                  </Link>
                ) : (
                  <span className={styles.factsRequesterName}>{getUserDisplayName(m.user, m.userId)}</span>
                )}
                <span className={styles.factsRequesterRole}>{m.role}</span>
              </div>
            </div>
          ))}
          { }
          {(stripSla || stripReaction || isStaffRole) && (
            <div className={styles.factsPill}>
              {stripSla ? (
                <div
                  className={cn(
                    styles.factsSeg,
                    stripTone === 'warning' && styles.factsSegWarning,
                    (stripTone === 'burning' || stripTone === 'breached') && styles.factsSegDanger,
                    stripTone === 'paused' && styles.factsSegPaused,
                  )}
                  role="progressbar"
                  aria-label={stripSla.kind === 'reaction' ? 'Срок ответа' : 'Срок решения'}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(slaProgress(stripSla) * 100)}
                  title={[slaChipTitle(stripSla), reactionText].filter(Boolean).join('. ')}
                >
                  <span className={styles.factsSegValue}>
                    <SlaHourglass progress={slaProgress(stripSla)} className={styles.hourglass} />
                    {slaShortLabel(stripSla)}
                  </span>
                  <span className={styles.factsSegCaption}>
                    {stripReaction &&
                      (stripReaction.breached ? (
                        <AlertCircle size={11} aria-hidden className={styles.factsReactionBad} />
                      ) : (
                        <CheckCircle2 size={11} aria-hidden className={styles.factsReactionOk} />
                      ))}
                    {slaStateCaption(stripSla)}
                  </span>
                </div>
              ) : stripReaction ? (
                <div className={styles.factsSeg} title={reactionText ?? undefined}>
                  <span
                    className={cn(
                      styles.factsSegValue,
                      stripReaction.breached ? styles.factsReactionBad : styles.factsReactionOk,
                    )}
                  >
                    {stripReaction.breached ? (
                      <AlertCircle size={15} aria-hidden />
                    ) : (
                      <CheckCircle2 size={15} aria-hidden />
                    )}
                    {stripReaction.spentMinutes != null ? formatWorkMinutes(stripReaction.spentMinutes) : 'дан'}
                  </span>
                  <span className={styles.factsSegCaption}>
                    {stripReaction.breached ? 'ответ с опозданием' : 'ответ в срок'}
                  </span>
                </div>
              ) : null}
              {isStaffRole && (
                <div className={styles.factsSeg}>
                  <span className={styles.factsSegValue}>
                    <PriorityMark priority={priority} />
                    {TICKET_PRIORITY_LABELS[priority]}
                  </span>
                  <span className={styles.factsSegCaption}>приоритет</span>
                </div>
              )}
              {isStaffRole && stripLevel && (
                <div className={styles.factsSeg}>
                  <span className={styles.factsSegValue}>
                    {stripLevel.label}
                    <span className={styles.factsPillMuted}>×{stripLevel.multiplier}</span>
                  </span>
                  <span className={styles.factsSegCaption}>сложность</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className={styles.layout}>
        { }
        <Scroller className={styles.main} viewportClassName={styles.mainViewport}>
          { }
          { }
          <div className={cn(styles.card, styles.summary)}>
            {!narrow && headNode}
            <Tabs defaultValue="description">
              <TabsList className={styles.summaryTabs}>
                <TabsTab value="description" className={styles.summaryTab}>
                  Описание
                </TabsTab>
                {mediaTabs.map((t) => (
                  <TabsTab key={t.value} value={t.value} className={styles.summaryTab}>
                    {t.label}
                    <TabsCount value={t.count} />
                  </TabsTab>
                ))}
              </TabsList>
              { }
              <TabsPanel value="description" className={cn(styles.tabPanel, styles.descriptionPanel)}>
                {descriptionText ? (
                  <RichText text={descriptionText} className={styles.summaryText} />
                ) : !hasFormAnswers(ticket!) ? (
                  <p className={styles.muted}>Описание не заполнено.</p>
                ) : null}
                <FormAnswers ticket={ticket!} />
              </TabsPanel>
              <TabsPanel value="media" className={styles.tabPanel}>
                <MediaTab media={sidebarMedia.media} galleryItems={sidebarMedia.galleryItems} />
              </TabsPanel>
              <TabsPanel value="links" className={styles.tabPanel}>
                <LinksTab links={sidebarMedia.links} />
              </TabsPanel>
              <TabsPanel value="files" className={styles.tabPanel}>
                <FilesTab rows={sidebarMedia.rows} />
              </TabsPanel>
              {legacyFiles.length > 0 && (
                <TabsPanel value="legacy" className={styles.tabPanel}>
                  <LegacyFilesTab files={legacyFiles} />
                </TabsPanel>
              )}
            </Tabs>
          </div>

          {canReassignAssignee && id && (
            <ReassignAssigneeDialog
              ticketId={id}
              assignee={reassigningAssignee}
              canReassignGroup={canReassignGroup}
              creatorId={ticket!.creatorId}
              onClose={() => setReassigningAssignee(null)}
              onDone={() => fetchTicket(id)}
            />
          )}

          {ticketMail.info?.channel === 'email' && <MailThreadCard info={ticketMail.info} />}



          { }
          {approvalsPending && (
            <ApprovalsSection
              round={approvalRounds[0]?.levels.length ? approvalRounds[0] : null}
              approvals={ticketApprovals}
              currentUserId={user?.id}
              loadingId={approvalLoading}
              canRemove={canRemoveApproval}
              canViewProfiles={canViewProfiles}
              removingId={removingApprovalId}
              removingRound={removingRound}
              onApprove={handleApprove}
              onReject={handleReject}
              onRemove={removeApproval}
              onRemoveRound={removeRound}
              liftedApprovalId={myApproval?.id ?? null}
            />
          )}
          { }
          {hasPermission('change_status') && !isOwnTicket && (
            <FolderGrantsCard ticketId={ticket!.id} />
          )}
          { }
          {needsRequesterReply && (
            <div className={styles.replyNeededBanner}>
              <MessageSquare size={16} aria-hidden />
              <span>
                <strong>Требуется ваш ответ.</strong> Поддержка ждёт уточнений — напишите сообщение
                в чат ниже, и заявка автоматически вернётся в работу.
              </span>
            </div>
          )}

          { }
          <ResolutionCallout ticket={ticket!} showRating={isStaffRole} />

          { }
          <ComplexityRequestCallout
            ticket={ticket!}
            isAgent={isAgent}
            canRateComplexity={canRateComplexity}
            complexityLevels={complexityLevels}
          />


          { }
          <div className={styles.chatCard} data-anchor="chat">
            <div className={styles.chatTabs} role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={chatTab === 'chat'}
                className={cn(styles.chatTab, chatTab === 'chat' && styles.chatTabActive)}
                onClick={() => setChatTab('chat')}
              >
                Чат
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={chatTab === 'history'}
                className={cn(styles.chatTab, chatTab === 'history' && styles.chatTabActive)}
                onClick={() => setChatTab('history')}
              >
                История
              </button>
              {showNotes && (
                <button
                  type="button"
                  role="tab"
                  aria-selected={chatTab === 'notes'}
                  className={cn(styles.chatTab, chatTab === 'notes' && styles.chatTabActive)}
                  onClick={() => setChatTab('notes')}
                >
                  Заметки
                  <TabsCount
                    value={notesList.length}
                    unread={chatTab === 'notes' ? 0 : notesUnread}
                  />
                </button>
              )}
              <div className={styles.chatTabsFilter} ref={setHistoryFilterSlot} />
            </div>

            <div className={styles.chatPanel} hidden={chatTab !== 'chat'}>
              {user?.id ? (
                messagesLoading ? (
                  <p className={styles.muted}>Загрузка чата...</p>
                ) : chatForbidden ? (
                  <p className={styles.muted}>Чат по этой заявке вам недоступен.</p>
                ) : chatError ? (
                  <p className={styles.muted}>
                    Не удалось загрузить чат.{' '}
                    <button
                      type="button"
                      className={styles.linkButton}
                      onClick={() => setChatReloadKey((k) => k + 1)}
                    >
                      Повторить
                    </button>
                  </p>
                ) : (
                  <TicketChat
                    ticketId={ticket!.id}
                    initialMessages={liveMessages.length ? liveMessages : messages}
                    currentUserId={user.id}
                    templateContext={templateContext}
                    canModifyMessages={!isTicketFinal(ticket)}
                    chatFrozen={isTicketFinal(ticket)}
                    onMessagesChange={setLiveMessages}
                    onNoteCreated={addNoteFromTemplate}
                    mail={ticketMail.bridge}
                    bare
                  />
                )
              ) : (
                <p className={styles.muted}>Войдите, чтобы участвовать в чате по заявке.</p>
              )}
            </div>
            {showNotes && user?.id && (
              <div className={styles.chatPanel} hidden={chatTab !== 'notes'}>
                {notesLoading ? (
                  <p className={styles.muted}>Загрузка заметок...</p>
                ) : notesError ? (
                  <p className={styles.muted}>
                    Не удалось загрузить заметки.{' '}
                    <button
                      type="button"
                      className={styles.linkButton}
                      onClick={() => setNotesReloadKey((k) => k + 1)}
                    >
                      Повторить
                    </button>
                  </p>
                ) : (
                  <TicketChat
                    mode="notes"
                    ticketId={ticket!.id}
                    initialMessages={notesList}
                    currentUserId={user.id}
                    canModifyMessages
                    active={chatTab === 'notes'}
                    readAt={notesReadAt}
                    onRead={advanceNotesRead}
                    onMessagesChange={setLiveNotes}
                    bare
                  />
                )}
              </div>
            )}
            {chatTab === 'history' && (
              <div className={styles.chatPanel}>
                <TicketHistory ticketId={ticket!.id} bare filterSlot={historyFilterSlot} />
              </div>
            )}
          </div>
          {ticketMail.dialogs}
        </Scroller>

        { }
        <Scroller
          element="aside"
          className={styles.sidebar}
          viewportClassName={styles.sidebarViewport}
        >
          {sideCards}
        </Scroller>
      </div>
      </TicketActionsProvider>
    </div>
  );
};
